import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Shield, AlertTriangle, CheckCircle2, XCircle, Info } from 'lucide-react';

const DataIntegrityPanel = ({ dataIntegrity }) => {
  if (!dataIntegrity) return null;

  const { 
    overall_integrity_score, 
    confidence_level, 
    red_flags,
    suspicious_findings,
    transparency_indicators,
    assessment_explanation
  } = dataIntegrity;

  // Determine color and icon based on score
  const getIntegrityColor = (score) => {
    if (score >= 80) return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-300', icon: CheckCircle2 };
    if (score >= 50) return { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-300', icon: AlertTriangle };
    return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-300', icon: XCircle };
  };

  const integrityStyle = getIntegrityColor(overall_integrity_score);
  const IntegrityIcon = integrityStyle.icon;

  const getConfidenceBadgeColor = (level) => {
    if (level === 'high') return 'bg-green-100 text-green-800 border-green-300';
    if (level === 'medium') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  return (
    <Card className={`border-2 ${integrityStyle.border} ${integrityStyle.bg}`}>
      <CardHeader className={`border-b ${integrityStyle.border}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className={`text-xl font-bold ${integrityStyle.color} flex items-center gap-2`}>
            <Shield className="w-5 h-5" />
            Data Integrity Assessment
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={getConfidenceBadgeColor(confidence_level)}>
              {confidence_level.toUpperCase()} Confidence
            </Badge>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Integrity Score</span>
            <span className={`text-2xl font-bold ${integrityStyle.color}`}>
              {overall_integrity_score}/100
            </span>
          </div>
          <Progress 
            value={overall_integrity_score} 
            className={`h-3 ${integrityStyle.bg}`}
          />
        </div>
      </CardHeader>
      
      <CardContent className="pt-6 space-y-4">
        {/* Assessment Explanation */}
        <div className="p-4 bg-white rounded-lg border-2 border-slate-200">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-slate-900 mb-1">Overall Assessment</h4>
              <p className="text-sm text-slate-700 leading-relaxed">{assessment_explanation}</p>
            </div>
          </div>
        </div>

        {/* Red Flags */}
        {red_flags && red_flags.length > 0 && (
          <Alert variant="destructive" className="bg-red-50 border-red-300">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong className="font-semibold">⚠️ Red Flags Detected:</strong>
              <ul className="mt-2 space-y-1 ml-4 list-disc">
                {red_flags.map((flag, idx) => (
                  <li key={idx} className="text-sm">{flag}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Suspicious Findings */}
        {suspicious_findings && Object.values(suspicious_findings).some(v => v) && (
          <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
            <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Suspicious Findings
            </h4>
            <div className="space-y-2">
              {suspicious_findings.sample_size_issues && (
                <div className="text-sm">
                  <span className="font-medium text-amber-900">Sample Size Issues:</span>
                  <p className="text-amber-800 ml-4">{suspicious_findings.sample_size_issues}</p>
                </div>
              )}
              {suspicious_findings.statistical_impossibilities && (
                <div className="text-sm">
                  <span className="font-medium text-amber-900">Statistical Concerns:</span>
                  <p className="text-amber-800 ml-4">{suspicious_findings.statistical_impossibilities}</p>
                </div>
              )}
              {suspicious_findings.unrealistic_results && (
                <div className="text-sm">
                  <span className="font-medium text-amber-900">Unrealistic Results:</span>
                  <p className="text-amber-800 ml-4">{suspicious_findings.unrealistic_results}</p>
                </div>
              )}
              {suspicious_findings.missing_critical_data && (
                <div className="text-sm">
                  <span className="font-medium text-amber-900">Missing Critical Data:</span>
                  <p className="text-amber-800 ml-4">{suspicious_findings.missing_critical_data}</p>
                </div>
              )}
              {suspicious_findings.selective_reporting_signs && (
                <div className="text-sm">
                  <span className="font-medium text-amber-900">Selective Reporting Signs:</span>
                  <p className="text-amber-800 ml-4">{suspicious_findings.selective_reporting_signs}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transparency Indicators */}
        {transparency_indicators && (
          <div className="p-4 bg-white rounded-lg border-2 border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-3">Transparency Indicators</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                {transparency_indicators.methodology_clearly_described ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm text-slate-700">Methodology Clearly Described</span>
              </div>
              
              <div className="flex items-center gap-2">
                {transparency_indicators.raw_data_availability === 'available' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : transparency_indicators.raw_data_availability === 'not_mentioned' ? (
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm text-slate-700">
                  Raw Data: {transparency_indicators.raw_data_availability || 'Not mentioned'}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {transparency_indicators.conflicts_of_interest_disclosed ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm text-slate-700">COI Disclosed</span>
              </div>
              
              <div className="flex items-center gap-2">
                {transparency_indicators.funding_source_disclosed ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm text-slate-700">Funding Disclosed</span>
              </div>
              
              <div className="flex items-center gap-2">
                {transparency_indicators.protocol_registration_mentioned ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-sm text-slate-700">Protocol Registered</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer Note */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <strong>📊 About This Assessment:</strong> This AI-powered integrity check examines methodology transparency, 
            statistical consistency, and reporting completeness. It does NOT replace peer review or expert evaluation. 
            Always consult the original article and additional sources before making clinical decisions.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DataIntegrityPanel;
